import { Injectable } from '@angular/core';
import { WhiteLabelService } from '../whiteLabel/white-label.service';
import { IWhitelabel, IWhitelabelFixedColors, IWhitelabelTheme } from '../../shared/models/i-whitelabel';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { IStoreData } from 'src/app/shared/models/i-store-data';
import { HttpErrorResponse } from '@angular/common/http';
import { EcoCostService } from '../eco-cost/eco-cost.service';
import { setRootStyleProperty } from 'src/app/shared/utils/rootStyleProperty.util';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private darkMode: boolean;
  private storeDataSubject = new BehaviorSubject<IStoreData>({
    urlLogo: '/assets/img/navbar/pro-reforma-branco.svg',
    storeName: 'Pró-Reforma'
  });

  whitelabelId: string = null;
  retailIds: string[] = [];

  constructor(private whitelabelService: WhiteLabelService, private ecoCostService: EcoCostService) { }

  getFixedColors(isWhiteLabel: boolean, isDarkMode: boolean) {
    const defaultFixedColors = {
      filter: isDarkMode ? "#FFFFFF" : "#5D5757",
      text: isDarkMode ? "#FFFFFF" : "#000000",
      acceptSelection: "#2CA900",
      formBackground: isDarkMode ? "#55504E" : "#E8E8E8",
      screenBackground: isDarkMode ? "#151414" : "#FFFFFF",
      review: isDarkMode ? "#FFD902" : "#FFE485"
    }

    const whitelabelFixedColors = {
      filter: isDarkMode ? "#C0D496" : "#5D5757",
      text: isDarkMode ? "#FFC38D" : "#000000",
      acceptSelection: "#2CA900",
      formBackground: isDarkMode ? "#406402" : "#E8E8E8",
      screenBackground: isDarkMode ? "#151414" : "#FFFFFF",
      review: isDarkMode ? "#FFD902" : "#FFE485"
    }

    return isWhiteLabel ? whitelabelFixedColors : defaultFixedColors;
  }

  getVariableColors(whitelabel: IWhitelabel | null, isWhiteLabel: boolean, isDarkMode: boolean) {
    const defaultTheme = {
      primaryBackground: "#D95536",
      primaryFont: "#FFFFFF",
      secondaryFont: isDarkMode ? "#E89987" : "#D95536",
      terciaryFont: "#D95536",
      primaryButtonDisabled: "#F1BFB4",
      primaryProCarbonoBackground: isDarkMode ? "#2C452E" : "#3E653F",
      primaryProCarbonoFont: "#FFFFFF",
      secondaryProCarbonoFont: isDarkMode ? "#457846" : "#3E653F",
      terciaryProCarbonoFont: isDarkMode ? "#FFFFFF" : "#3E653F",
      primaryProCarbonoButtonDisabled: "#AFC6B0"
    };

    const whitelabelTheme = {
      primaryBackground:
        isDarkMode ? whitelabel?.darkPrimaryBackground : whitelabel?.primaryBackground,
      primaryFont:
        isDarkMode ? whitelabel?.darkPrimaryFont : whitelabel?.primaryFont,
      secondaryFont:
        isDarkMode ? whitelabel?.darkSecondaryFont : whitelabel?.secondaryFont,
      terciaryFont:
        isDarkMode ? whitelabel?.darkTerciaryFont : whitelabel?.terciaryFont,
      primaryButtonDisabled:
        isDarkMode ? whitelabel?.darkPrimaryButtonDisabled : whitelabel?.primaryButtonDisabled,
      primaryProCarbonoBackground:
        isDarkMode ? whitelabel?.darkPrimaryProCarbonoBackground : whitelabel?.primaryProCarbonoBackground,
      primaryProCarbonoFont:
        isDarkMode ? whitelabel?.darkPrimaryProCarbonoFont : whitelabel?.primaryProCarbonoFont,
      secondaryProCarbonoFont:
        isDarkMode ? whitelabel?.darkSecondaryProCarbonoFont : whitelabel?.secondaryProCarbonoFont,
      terciaryProCarbonoFont:
        isDarkMode ? whitelabel?.darkTerciaryProCarbonoFont : whitelabel?.terciaryProCarbonoFont,
      primaryProCarbonoButtonDisabled:
        isDarkMode ? whitelabel?.darkPrimaryProCarbonoButtonDisabled : whitelabel?.primaryProCarbonoButtonDisabled
    }

    return isWhiteLabel ? whitelabelTheme : defaultTheme;
  }

  getStoreInfo(whitelabel: IWhitelabel | null, isWhiteLabel: boolean): IStoreData {
    const defaultStoreData = {
      urlLogo: '/assets/img/navbar/pro-reforma-branco.svg',
      storeName: 'Pró-Reforma'
    };

    const whitelabelStoreData = {
      urlLogo: whitelabel?.urlBrandLogo ?? '/assets/img/navbar/pro-reforma-branco.svg',
      storeName: whitelabel?.brandDomain ?? 'Pró-Reforma'
    };

    return isWhiteLabel ? whitelabelStoreData : defaultStoreData;
  }

  getTheme(url: string, isDarkMode: boolean): Observable<any> {
    return this.whitelabelService.getWhitelabelByDomain(url).pipe(
      map((response) => {
        const whitelabel = response.result;

        return this.createTheme(whitelabel, true, isDarkMode);
      }),
      catchError((err) => {
        if(err instanceof HttpErrorResponse && err.status !== 404) {
          console.error('Erro ao buscar o whitelabel:', err);
        }

        return of(this.createTheme(null, false, isDarkMode));
      })
    )
  }

  loadTheme(url: string, onSuccess?: () => void, isDarkMode: boolean = false) {
    if (isDarkMode) this.darkMode = isDarkMode;

    return this.getTheme(url, this.darkMode).subscribe((theme) => {
      const variableColors = {
        primaryBackground: theme.primaryBackground,
        primaryFont: theme.primaryFont,
        secondaryFont: theme.secondaryFont,
        terciaryFont: theme.terciaryFont,
        primaryButtonDisabled: theme.primaryButtonDisabled,
        primaryProCarbonoBackground: theme.primaryProCarbonoBackground,
        primaryProCarbonoFont: theme.primaryProCarbonoFont,
        secondaryProCarbonoFont: theme.secondaryProCarbonoFont,
        terciaryProCarbonoFont: theme.terciaryProCarbonoFont,
        primaryProCarbonoButtonDisabled: theme.primaryProCarbonoButtonDisabled
      };
      const fixedColors = {
        filter: theme.filter,
        text: theme.text,
        acceptSelection: theme.acceptSelection,
        formBackground: theme.formBackground,
        screenBackground: theme.screenBackground,
        review: theme.review
      };

      //informações da loja
      this.storeDataSubject.next({urlLogo: theme.urlLogo, storeName: theme.storeName});

      this.updateFixedColors(fixedColors);
      this.updateVariableColors(variableColors);

      this.ecoCostService.loadTheme(variableColors);

      onSuccess && onSuccess();
    });
  }

  getStoreData(): Observable<IStoreData>{
    return this.storeDataSubject.asObservable();
  }

  isDarkMode(): boolean {
    return this.darkMode;
  }

  createTheme(whitelabel: IWhitelabel | null, isWhiteLabel: boolean, isDarkMode: boolean) {
    const storeInfo = this.getStoreInfo(whitelabel, isWhiteLabel);
    const fixedColors = this.getFixedColors(isWhiteLabel, isDarkMode);
    const variableColors = this.getVariableColors(whitelabel, isWhiteLabel, isDarkMode);

    this.whitelabelId = whitelabel ? whitelabel.id : null;
    this.retailIds = whitelabel ? whitelabel.retail : [];

    return { ...storeInfo, ...variableColors, ...fixedColors };
  }

  updateVariableColors(theme: Partial<IWhitelabelTheme>): void {
    const variableColors = {
      '--primary-background': theme.primaryBackground,
      '--primary-font': theme.primaryFont,
      '--secondary-font': theme.secondaryFont,
      '--terciary-font': theme.terciaryFont,
      '--primary-button-disabled': theme.primaryButtonDisabled,
      '--primary-pro-carbono-background': theme.primaryProCarbonoBackground,
      '--primary-pro-carbono-font': theme.primaryProCarbonoFont,
      '--secondary-pro-carbono-font': theme.secondaryProCarbonoFont,
      '--terciary-pro-carbono-font': theme.terciaryProCarbonoFont,
      '--primary-pro-carbono-button-disabled': theme.primaryProCarbonoButtonDisabled
    }

    Object.entries(variableColors).forEach(([key, value]) => {
      setRootStyleProperty(key, value);
    });
  }

  updateFixedColors(theme: IWhitelabelFixedColors): void {
    const fixedColors = {
      '--filter': theme.filter,
      '--text': theme.text,
      '--accept-selection': theme.acceptSelection,
      '--form-background': theme.formBackground,
      '--screen-background': theme.screenBackground,
      '--review': theme.review
    }

    Object.entries(fixedColors).forEach(([key, value]) => {
      setRootStyleProperty(key, value);
    });
  }
}
